import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, from, timeout } from 'rxjs';
import { AuthTokenError, AuthService } from './auth.service';
import { UserService } from './user.service';
import { LoaderService } from './loader.service';
import { SessionRecoveryService } from './session-recovery.service';
import { ROLES, ROLE_INFO, COMPANY_NAME } from '../constants';
import { classifyStartupResponse, extractOutcomeCode, isStaticStartupRoute, StartupFailureKind } from './startup-failure';

/** One startup sequence per document; all callers share the same business identity. */
@Injectable({ providedIn: 'root' })
export class StartupService {
  private pending?: Promise<boolean>;
  private identityReady = false;
  private sidebarReady = false;
  private questionnaireReady = false;
  private businessNavigationCompleted = false;
  private terminal = false;
  get hasTerminalFailure(): boolean { return this.terminal; }

  constructor(private auth: AuthService, private user: UserService,
    private router: Router, private loader: LoaderService,
    private recovery: SessionRecoveryService) {}

  ensureReady(): Promise<boolean> {
    if (this.terminal) return Promise.resolve(false);
    return this.pending ??= this.loadIdentity();
  }

  private async loadIdentity(): Promise<boolean> {
    this.loader.setLoadingState({ global: true });
    try {
      const bearer = await firstValueFrom(from(this.auth.ensureApiBearer()).pipe(timeout(35000)));
      if (!bearer) { this.fail('auth-recovery', 'auth_required'); return false; }
      const roles = await firstValueFrom(this.user.getUserRoles().pipe(timeout(45000)));
      if (roles?.needToUpdateSession) { this.fail('auth-recovery', 'session_update_required'); return false; }
      const brand = await firstValueFrom(this.user.getUserBrandName().pipe(timeout(45000)));
      if (!brand?.brandName) { this.fail('access-denied', 'brand_not_found'); return false; }
      if (this.terminal) return false;
      localStorage.setItem(ROLES, JSON.stringify(roles?.roles ?? []));
      localStorage.setItem(COMPANY_NAME, brand.brandName);
      localStorage.setItem(ROLE_INFO, JSON.stringify({ role: brand.roleName, id: brand.roleId }));
      this.identityReady = true;
      this.auth.ensureBusinessClockLoaded();
      return true;
    } catch (error) {
      this.handleFailure(error);
      return false;
    } finally { this.loader.setLoadingState({ global: false }); }
  }

  handleFailure(error: unknown): void {
    if (error instanceof AuthTokenError) {
      if (error.code === 'interaction_started' || error.code === 'session_ended') {
        this.loader.setLoadingState({ global: false });
        return;
      }
      const code = error.code === 'account_ambiguous' ? 'multiple_matching_accounts'
        : error.code === 'session_refresh_required' ? 'session_update_required' : 'auth_required';
      this.fail(error.code === 'temporarily_unavailable' ? 'system-unavailable' : 'auth-recovery', code);
      return;
    }
    this.fail(classifyStartupResponse(error), extractOutcomeCode(error));
  }

  fail(kind: StartupFailureKind, outcomeCode = ''): void {
    this.loader.setLoadingState({ global: false });
    if (this.terminal) return;
    this.terminal = true;
    const route = kind === 'system-unavailable' ? '/system-unavailable'
      : kind === 'access-denied' || kind === 'auth-recovery' ? '/access-denied' : '/startup-error';
    void this.router.navigate([route], {
      state: { outcomeCode: outcomeCode || (kind === 'auth-recovery' ? 'auth_required' : '') },
      replaceUrl: true,
    });
  }

  markSidebarReady(): void { this.sidebarReady = true; this.completeIfReady(); }
  markQuestionnaireReady(): void { this.questionnaireReady = true; this.completeIfReady(); }
  confirmNavigation(url: string): void {
    this.businessNavigationCompleted = !!url.split(/[?#]/)[0].split('/').filter(Boolean).length
      && !isStaticStartupRoute(url) && url.split('/')[1] !== 'dome';
    this.completeIfReady();
  }
  private completeIfReady(): void {
    if (!this.terminal && this.identityReady && this.sidebarReady && this.questionnaireReady
      && this.businessNavigationCompleted) this.recovery.clearRecoveryMarker();
  }
}
